import '@/configs/firebase-firestore';
import { FirebaseError } from 'firebase/app';
import { collection, doc, getFirestore, setDoc } from 'firebase/firestore';
import { FunctionsError } from 'firebase/functions';
import { noop } from 'lodash-es';

import type { UserId } from '@scaffold/common/user';

import { ComponentError } from '@/core/component-error';
import { useAuthStore } from '@/stores/auth';

type LogRecord = {
  uid: UserId | null;
  severity: 'info' | 'warn' | 'error';
  summary: string;
  body: string;
};

function getClientInfoSafely() {
  try {
    return {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      screen: `${screen.width}x${screen.height}`,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      online: navigator.onLine,
    };
  } catch {
    return null;
  }
}

export async function pushLog(record: LogRecord) {
  return setDoc(doc(collection(getFirestore(), 'logs')), {
    ...record,
    url: window.location.href,
    time: new Date(),
    version: `${__APP_VERSION__}-${__BUILD_TIME__}`,
    client: getClientInfoSafely(),
  }).catch(noop);
}

export function pushLogError(severity: LogRecord['severity'], error: unknown) {
  try {
    const err = error instanceof ComponentError ? error.err : error;

    let details: Record<string, unknown> | undefined;
    if (err instanceof FunctionsError) {
      details = { code: err.code, details: err.details };
    } else if (err instanceof FirebaseError) {
      details = { code: err.code };
    }

    console.error(err, {
      ...(error instanceof ComponentError
        ? { component: error.instance, info: error.info }
        : undefined),
      ...details,
    });

    if (import.meta.env.PROD) {
      function stringifySafe(obj: unknown): string {
        try {
          return JSON.stringify(obj, null, 2);
        } catch {
          return '';
        }
      }

      pushLog({
        severity,
        uid: useAuthStore().uid ?? null,
        summary: String(err),
        body: stringifySafe({
          ...(error instanceof ComponentError
            ? {
                component: error.instance?.$options.name,
              }
            : undefined),
          ...details,
        }),
      });
    }
  } catch (err) {
    console.error('Error handling', err);
  }
}
